import React, {Fragment} from 'react';

import {Box, Button, capitalize, Card, Stack, Typography} from '@mui/material';
import {OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {
	AlertInfo,
	CopySection,
	CTAButtonFixed,
	MainFooter,
	MainHeader,
	StyledGrid,
} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import Icon from 'presentation/icons';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {AppointmentInstructions} from 'presentation/modules/booking/shared/ui/components/AppointmentInstructions';
import {ConfirmationCard} from 'presentation/modules/booking/shared/ui/components/ConfirmationCard';
import {ProtocolCard} from 'presentation/modules/booking/shared/ui/components/ProtocolCard';
import {SquareLabelTime} from 'presentation/modules/booking/shared/ui/components/SquareLabelTime';
import {SaveToCalendar} from 'presentation/pages/private/home/components';
import {PrintTemplate} from 'presentation/modules/myAppointments/ui/components/PrintTemplate';
import {translations} from 'presentation/translations-new';
import {useOdontoConfirmationBookingContent} from './hooks/useOdontoConfirmationBookingContent';
import {StyledBox, StyledStack} from './styles';
import {OdontoConfirmationBookingContentProps} from './types';
import {WhoIndicatesCard} from 'presentation/components/WhoIndicatesCard';
import {WhoIndicatesTagParametricEnum} from 'domain/usecases/private/home/who-indicates';

const {at, attendanceUnit, phones, download, professionalAndDate, referAndEarn} =
	translations['pt-br'].commons;
const {openMap} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow.unitDetails;
const {confirmationBooking, seeMyAppointments, appointmentSummary} =
	translations['pt-br'].privateFlow.bookingFlow;

const {appointmentCity} = translations['pt-br'].privateFlow.profile;

export function OdontoConfirmationBookingContent({
	bookedAppointment,
	goToMyAppointments,
}: OdontoConfirmationBookingContentProps) {
	const {
		isMobile,
		isTablet,
		betweenMobileAndDesktop,
		isPrinting,
		printContent,
		printRef,
		iconName,
		alertTitle,
		calendarEvent,
		colors,
		clinicName,
		formattedAddress,
		formattedDateWithWeekday,
		hour,
		phoneNumbers,
		providerName,
		specialtyName,
		protocolNumber,
		day,
		monthName,
		handleOpenGoogleMaps,
		addressFormatted,
		hasPhoneNumbers,
		isDesktop,
	} = useOdontoConfirmationBookingContent(bookedAppointment);

	return (
		<Fragment>
			<MainHeader
				title={confirmationBooking}
				handleBack={goToMyAppointments}
				handleClose={goToMyAppointments}
				variant="subRoute">
				<Card sx={{borderRadius: 0, boxShadow: 'none', border: 0}}>
					<AlertInfo.Root severity="success" sx={{borderRadius: 0, border: 0}}>
						<AlertInfo.Title title={alertTitle} sx={{mb: 0}} />
					</AlertInfo.Root>
				</Card>
			</MainHeader>

			<StyledGrid.Root p={isMobile ? 2 : 4}>
				<StyledGrid.Item sm={12} md={12} lg={8}>
					<Stack gap={2}>
						<Typography variant="subtitle2" color={'text.secondary'}>
							{appointmentSummary}
						</Typography>
						<ConfirmationCard.Root>
							<ConfirmationCard.Header subheader={professionalAndDate}>
								<Stack flexDirection={'row'} gap={2} alignItems={'start'}>
									<SquareLabelTime day={day} month={monthName} />
									<Stack width={'100%'} gap={'2px'}>
										<Typography variant="subtitle2" fontSize={'1.125rem'}>
											{`${capitalize(formattedDateWithWeekday)} ${at} ${hour}`}
										</Typography>
										<ConfirmationCard.Content
											iconName="medical-services-filled"
											primaryText={providerName}
											disableMargin
										/>
										<ConfirmationCard.Content
											iconName={iconName}
											primaryText={specialtyName}
											disableMargin
										/>
									</Stack>
								</Stack>
								<StyledStack className="remove-me">
									<Button
										disabled={isPrinting}
										variant="outlined"
										endIcon={
											isPrinting ? (
												<Loading size={24} color="inherit" />
											) : (
												<Icon
													name="download"
													color={colors.brand.primary.blue}
													size={24}
												/>
											)
										}
										fullWidth={isMobile}
										onClick={printContent}
										data-tag-id={
											OdontoBookingTaggingEnum.CLICK_BAIXAR_AGENDAMENTO
										}>
										{download}
									</Button>
									<SaveToCalendar
										dataTagId={OdontoBookingTaggingEnum.CLICK_SALVAR_NA_AGENDA}
										calendarEvent={calendarEvent}
									/>
								</StyledStack>
							</ConfirmationCard.Header>
						</ConfirmationCard.Root>
						<Box
							display="grid"
							gridTemplateColumns={isTablet ? '1fr' : '1fr 1fr'}
							gap={2}>
							<ConfirmationCard.Root>
								<ConfirmationCard.Header subheader={attendanceUnit} />
								<Stack
									flexDirection={'column'}
									alignItems={'start'}
									justifyContent="space-between"
									gap={1}>
									<ConfirmationCard.Content
										iconName={'location-on-filled'}
										primaryText={clinicName}
										secondaryText={formattedAddress}
									/>

									<StyledStack className="remove-me">
										<Button
											sx={{minWidth: 'fit-content'}}
											onClick={handleOpenGoogleMaps}
											fullWidth={isTablet}
											variant="outlined"
											endIcon={<Icon name="map" />}>
											{openMap}
										</Button>
									</StyledStack>
								</Stack>
							</ConfirmationCard.Root>

							<ConfirmationCard.Root>
								<ConfirmationCard.Header subheader={appointmentCity} />
								<ConfirmationCard.Content
									iconName={'my-location-filled'}
									primaryText={addressFormatted.first}
									secondaryText={addressFormatted.second}
								/>
							</ConfirmationCard.Root>
						</Box>

						<ProtocolCard protocolNumber={protocolNumber} />

						{hasPhoneNumbers ? (
							<ConfirmationCard.Root>
								<ConfirmationCard.Header subheader={phones} />
								<Stack direction={isTablet ? 'column' : 'row'} spacing={1}>
									{phoneNumbers?.map((phoneNumber, index) => (
										<CopySection
											key={`${index}-${phoneNumber}`}
											value={phoneNumber}
										/>
									))}
								</Stack>
							</ConfirmationCard.Root>
						) : null}

						{betweenMobileAndDesktop ? null : (
							<CTAButtonFixed.Root>
								<Stack gap={1} direction={'row'} justifyContent="flex-end">
									<CTAButtonFixed.Action
										onClick={goToMyAppointments}
										fullWidth={isMobile}
										size="large"
										data-tag-id={
											OdontoBookingTaggingEnum.CLICK_VER_MEUS_AGENDAMENTOS
										}>
										{seeMyAppointments}
									</CTAButtonFixed.Action>
								</Stack>
							</CTAButtonFixed.Root>
						)}
					</Stack>
				</StyledGrid.Item>

				<StyledGrid.Item sm={12} md={12} lg={4}>
					<AppointmentInstructions appointment={bookedAppointment} />

					<Stack gap={2} mt={2}>
						<Typography
							fontWeight={600}
							variant="body2"
							color={colors.light.text.secondary}>
							{referAndEarn}
						</Typography>
						<WhoIndicatesCard
							dataTagId={OdontoBookingTaggingEnum.CLICK_SAIBA_MAIS_QUEM_INDICA}
							$showAsBanner={!isMobile && !isDesktop}
							pathParametric={WhoIndicatesTagParametricEnum.BOOKING}
						/>
					</Stack>

					{betweenMobileAndDesktop ? (
						<Box mt={2}>
							<Stack py={1} alignItems="flex-end">
								<Button
									onClick={goToMyAppointments}
									size="large"
									data-tag-id={
										OdontoBookingTaggingEnum.CLICK_VER_MEUS_AGENDAMENTOS
									}>
									{seeMyAppointments}
								</Button>
							</Stack>
						</Box>
					) : null}
				</StyledGrid.Item>
			</StyledGrid.Root>

			<StyledBox>
				<MainFooter />
			</StyledBox>

			<PrintTemplate
				bookedAppointment={bookedAppointment}
				flowType={FlowEnum.ODONTO}
				printRef={printRef}
			/>
		</Fragment>
	);
}
